import { MRT_ColumnDef } from 'mantine-react-table';
import { Flex, Text } from '@mantine/core';
import type { CSSProperties } from 'react';
import { formatDateToShow, stringToDate } from 'app/utils/util-functions';
import { WireInstructionParams } from 'app/models/wire-instruction-params';

const getTextStyle = (weight: number, lineHeight: string, textAlign?: 'start' | 'end' | 'center', color?: string): CSSProperties => ({
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '14px',
    fontWeight: weight,
    lineHeight: lineHeight,
    textAlign: textAlign ?? 'center',
    color: color,
    width: '100%',
});
// #413E3E

function TableHead({ header, textAlign }: { header: string, textAlign?: 'start' | 'end' | 'center' }) {
    return (
        <Text style={getTextStyle(700, '22px', textAlign, '#413E3E')}>
            {header}
        </Text>
    );
}

export const columnDefsWireInstructionDrawer: MRT_ColumnDef<WireInstructionParams>[] = [
    {
        header: 'Currency',
        size: 55,
        accessorKey: 'currency',
        Header: () => (
            <TableHead header={'Currency'} textAlign='start' />
        ),
        Cell: ({ row }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{row.original.currency}</Text>

                </div>
            );
        },
    },

    {
        header: 'ABA/IBAN',
        size: 55,
        accessorKey: 'iban',
        Header: () => (
            <TableHead header={'ABA/IBAN'} textAlign='start' />
        ),
        Cell: ({ row }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{row.original.iban}</Text>

                </div>
            );
        },
    },

    {
        header: 'Account #',
        size: 55,
        accessorFn: (row) =>
            row.beneficiaryAccountNumber?.trim() ||
            row.creditorAccount?.trim() ||
            '',
        Header: () => (
            <TableHead header={'Account #'} textAlign='start' />
        ),
        Cell: ({ cell }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{cell.getValue<string>()}</Text>

                </div>
            );
        },
    },

    {
        header: 'Beneficiary Account #',
        size: 55,
        accessorKey: 'beneficiaryAccountNumber',
        Header: () => (
            <TableHead header={'Beneficiary Account #'} textAlign='start' />
        ),
        Cell: ({ row }) => {
            return (
                <div>
                    <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{row.original.beneficiaryAccountNumber}</Text>

                </div>
            );
        },
    },

    {
        size: 100,
        accessorKey: 'effectiveDate',
        header: 'Effective Date',
        Header: <TableHead header={'Effective Date'} />,
        accessorFn: row => {
            return row.effectiveDate ? stringToDate(row.effectiveDate || '') : null;
        },
        mantineFilterTextInputProps: {
            placeholder: 'Date',
        },
        filterFn: 'lessThanOrEqualTo',
        filterVariant: 'date',
        sortingFn: 'datetime',
        Cell: ({ cell }) => (
            <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{formatDateToShow(cell.getValue<Date>())}</Text>

        )
    },
    {
        size: 100,
        accessorKey: 'endDate',
        header: 'Expiration Date',
        Header: <TableHead header={'Expiration Date'} />,
        accessorFn: row => {
            return row.endDate ? stringToDate(row.endDate || '') : null;
        },
        mantineFilterTextInputProps: {
            placeholder: 'Date',
        },
        filterFn: 'lessThanOrEqualTo',
        filterVariant: 'date',
        sortingFn: 'datetime',
        Cell: ({ cell }) => (
            <Text style={{ ...getTextStyle(600, '19px', 'start', '#413E3E') }}>{formatDateToShow(cell.getValue<Date>())}</Text>
        ),
    },

    {
        size: 100,
        accessorKey: 'usedFor',
        header: 'Usage Tags',
        Header: <TableHead header={'Usage Tags'} />,
        Cell: ({ cell }) => {
            let items = cell.getValue<string[]>() || [];
            if (
                items.includes('TradesWI') &&
                items.includes('PrincipalInterestWI')
            ) {
                items = ['allPayments'];
            }

            if (items.includes('allPayments')) {
                items = ['allPayments'];
            }
            return (
                <Flex wrap="wrap" gap="10px">
                    {items.map((value: string) => {
                        // human-readable label
                        const label =
                            value === 'TradesWI'
                                ? 'Trades'
                                : value === 'PrincipalInterestWI'
                                    ? 'Principal/Interest'
                                    : value === 'allPayments'
                                        ? 'All Payments'
                                        : value;

                        return (
                            <div
                                key={value}
                                style={{
                                    display: 'flex',
                                    padding: '6px 12px',
                                    alignItems: 'center',
                                    gap: '10px',
                                    borderRadius: '5px',
                                    background: '#E9F1FF',
                                    color: '#757373',
                                    fontFamily: 'Inter',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    lineHeight: 'normal',
                                }}
                            >
                                {label}
                            </div>
                        );
                    })}
                </Flex>
            );
        },
    },
    {
        header: 'Status',
        size: 55,
        accessorKey: 'status',
        Header: () => <TableHead header="Status" textAlign="start" />,
        Cell: ({ row }) => {
            const status = row.original.status;
            const color = status === 'Active' ? '#287A26' : '#CB3232';

            return (
                <Text
                    style={{
                        ...getTextStyle(600, '19px', 'start'), color,
                    }}
                >
                    {status}
                </Text>
            );
        },
    },
];
